import type { ReactNode } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { Check, ExpandLess, ExpandMore } from '@dhl/icons'
import { Fullbleed } from '@dhl/components'
import { useIsSmallScreen } from '~/hooks'
import { borders } from '~/components'

type Props = {
  title: string
  valueLabel: string
  isValid: boolean
  sm: boolean
  expanded: boolean
  isRightSection?: boolean
  children: ReactNode
  onToggleExpand: (expand: boolean) => void
}

const Status = ({ small }: { small?: boolean }) => (
  <Check htmlColor="green" fontSize={small ? 'small' : 'inherit'} sx={small ? { marginLeft: 1 } : {}} />
)

export const Section = ({
  title,
  sm,
  valueLabel,
  isValid,
  isRightSection,
  expanded,
  children,
  onToggleExpand,
}: Props) => {
  const isSmallScreen = useIsSmallScreen()
  const handleClick = () => {
    if (isSmallScreen) {
      onToggleExpand(!expanded)
    }
  }

  return (
    <Box
      sx={({ palette: { grey } }) => ({
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isRightSection ? grey[300] : 'inherit',
        width: sm ? '50%' : 'auto',
      })}
    >
      <Box
        onClick={handleClick}
        sx={({ palette: { grey } }) => ({
          userSelect: 'none',
          height: '56px',
          paddingTop: 1.5,
          paddingLeft: 2,
          paddingRight: 2,
          display: 'flex',
          flexDirection: 'row',
          cursor: isSmallScreen ? 'pointer' : 'default',
          ...borders(['top'], grey[300]),
        })}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
            <Typography variant="h5">{title}</Typography>
            {isSmallScreen && isValid && <Status small />}
          </Box>
          <Box sx={({ palette: { grey } }) => ({ color: grey[500] })}>
            <Typography variant="body2">{valueLabel}</Typography>
          </Box>
        </Box>
        <Box sx={{ paddingTop: 0.5 }}>
          {!isSmallScreen && isValid && <Status />}
          {isSmallScreen && (
            <Fullbleed sx={{ marginTop: '-10px', marginRight: '-14px' }}>
              <IconButton onClick={handleClick} size="large">
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Fullbleed>
          )}
        </Box>
      </Box>
      <Box
        sx={({ palette: { grey } }) => ({
          height: '1px',
          marginLeft: isSmallScreen ? 'inherit' : 2,
          marginRight: isSmallScreen ? 'inherit' : 2,
          borderBottom: `solid  1px ${grey[isRightSection ? 400 : 300]}`,
        })}
      />
      <Box
        sx={{
          overflow: 'hidden',
          height: expanded ? '330px' : 0,
          transition: 'height 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}
